import React, { useEffect, useRef } from 'react';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { useHistory, useParams } from 'react-router-dom';
import { FiHome, FiUsers, FiCode, FiGlobe, FiUser, FiFileText, FiLoader } from 'react-icons/fi';
import MegaphoneIcon from '../MegaphoneIcon';
import EmptyState from '../EmptyState';
import { useClient } from './clientSettingsData';
import Company from './Company';
import RegistryConnection from './RegistryConnection';
import UsersAccess from './UsersAccess';
import Billing from './Billing';
import WordPressConnection from './WordPressConnection';
import Widgets from './widgets/Widgets';
import AnnouncementSettings from './announcements/AnnouncementSettings';

// "later" = built in a later phase; the tab is there so the row matches the spec
const tabs: { path: string; label: string; icon: JSX.Element; badge?: string; component?: React.FC; later?: string }[] = [
    { path: '', label: 'Company', icon: <FiHome />, component: Company },
    { path: 'announcements', label: 'Announcement Settings', icon: <MegaphoneIcon />, component: AnnouncementSettings },
    { path: 'registry', label: 'Registry Connection', icon: <FiUsers />, component: RegistryConnection },
    { path: 'widgets', label: 'Widgets', icon: <FiCode />, badge: '9/11', component: Widgets },
    { path: 'wordpress', label: 'WordPress', icon: <FiGlobe />, component: WordPressConnection },
    { path: 'users', label: 'Users & Access', icon: <FiUser />, badge: '4', component: UsersAccess },
    { path: 'billing', label: 'Billing', icon: <FiFileText />, component: Billing },
];

// Discard + Save Changes; left out on pages with nothing to save (Billing)
interface ActionsProps {
    dirty?: boolean;
    // a real save is in flight (Company): spinner + both buttons disabled
    saving?: boolean;
    onSave?: () => void;
    onDiscard?: () => void;
}

interface HeaderProps extends ActionsProps {
    title: string;
    sub: string;
}

// page title + actions; on mobile the actions move to the end of the page (the page renders <SettingsActions> again there)
export const SettingsHeader: React.FC<HeaderProps> = ({ title, sub, ...actions }) => {
    const { name } = useClient();
    return <div className="page-header">
        <div>
            <h1>{title} — {name}</h1>
            <p>{sub}</p>
        </div>
        {actions.onSave && <div className="action-top"><SettingsActions {...actions} /></div>}
    </div>;
};

export const SettingsActions: React.FC<ActionsProps> = ({ dirty, saving, onSave, onDiscard }) => <>
    <button type="button" className="btn-outline" onClick={onDiscard} disabled={!dirty || saving}>Discard</button>
    <button type="button" className="btn-primary" onClick={onSave} disabled={!dirty || saving}>
        {saving && <FiLoader className="spin" />} {saving ? 'Saving…' : 'Save Changes'}
    </button>
</>;

export const RecordBanner: React.FC = () =>
    <div className="banner">
        <span className="icon-tile"><ShieldCheckIcon /></span>
        <div>
            <div className="title">Investi looks after the company record</div>
            <div className="hint">Company name, ASX code and exchange are set by Investi when the client is added. Everything else on this page is yours to change — message support if a detail needs correcting.</div>
        </div>
    </div>;

// standard subtitle for the editable pages
export const useSettingsSub = () => {
    const { short } = useClient();
    return `How Investi is set up for this client. Changes apply to everyone who works on ${short}.`;
};

const ComingLater: React.FC<{ what: string; title: string }> = ({ what, title }) => {
    const sub = useSettingsSub();
    return <>
        <SettingsHeader title={title} sub={sub} />
        <section className="card coming-later">
            <EmptyState art="caughtUp" title={what} sub="This part of Client settings is being built in the next phase." />
        </section>
    </>;
};

const ClientSettings: React.FC = () => {
    const history = useHistory();
    const { tab = '' } = useParams<{ tab?: string }>();
    const { stock } = useClient();
    const current = tabs.find(x => x.path === tab) ?? tabs[0];

    // tablet / mobile: the tab row scrolls sideways, so bring the active tab into view (same as Notifications)
    const tabsRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const row = tabsRef.current;
        const active = row?.querySelector<HTMLElement>('button.active');
        if (!row || !active || row.scrollWidth <= row.clientWidth) return;
        row.scrollTo({ left: active.offsetLeft - (row.clientWidth - active.offsetWidth) / 2, behavior: 'smooth' });
    }, [current]);

    return <div className="v2-page account-v2 client-settings-v2">
        <div className="sub-tabs" ref={tabsRef}>
            {tabs.map(x => <button key={x.path} type="button" className={x === current ? 'active' : ''}
                onClick={() => history.push(`/client-settings${x.path && `/${x.path}`}`)}>
                {x.icon} {x.label} {x.badge && <span className="badge">{x.badge}</span>}
            </button>)}
        </div>

        {/* the pages print the client's name, so wait for state.currentStock */}
        {stock && (current.component
            ? <current.component />
            : <ComingLater what={current.later ?? ''} title={current.label} />)}
    </div>;
};

export default ClientSettings;
